import { Box, Stack, styled } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import AgentCard from './AgentCard';
import CreateNewAgentCard from './CreateNewAgentCard';
import useAiAgents from './useAiAgents';
import { AgentType } from '@server/ai';
import ToggleExpandButton from './ToggleExpandButton';
import ClosePanelButton from './ClosePanelButton';
import useAgentsToRender from './useAgentsToRender';
import useAgentsChats from './useAgentsChats';
import AgentCardSkeleton from './AgentCardSkeleton';
import { UserSupportAgentCard } from './userSupport/UserSupportAgentCard';
import NewAgentButton from './NewAgentButton';
import usePopover from '../../hooks/usePopover';

const AGENT_CARDS_CONTAINER_WIDTH = '1192px';

const AgentsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '24px',
  width: '100%',
  '& > *': {
    minWidth: 0,
    width: '100%',
  },
});

const AgentsHomeCardsExpanded = ({
  closePanel,
  onNewAgentClick,
  isPageView,
}: {
  closePanel?: () => void;
  onNewAgentClick: (type: AgentType) => void;
  isPageView?: boolean;
}) => {
  const { setAgentClickHandler } = useAgentsChats();
  const { agentsIsLoading } = useAiAgents();
  const agentsToRender = useAgentsToRender();
  const menuPopoverProps = usePopover();
  const {
    existingAgents,
    creatableAgentTypes,
    singletonAgentsNoSub,
    creatableAgentTypesNoSub,
    canCreateNewAgents,
  } = agentsToRender ?? {};

  const renderLoading = agentsIsLoading && !agentsToRender;

  const existingAgentsHaveCreatableTypes = existingAgents?.some((agent) =>
    creatableAgentTypes?.includes(agent.type),
  );
  const showCreateNewAgentsSection = !existingAgentsHaveCreatableTypes;

  return (
    <Stack
      alignItems="center"
      width="100%"
      height="100%"
      sx={{
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {!isPageView && (
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            position: 'absolute',
            top: '12px',
            right: '12px',
            zIndex: 1, //To be on top of the cards
          }}
        >
          <ToggleExpandButton />
          <ClosePanelButton closePanel={closePanel} />
        </Box>
      )}

      <Stack
        sx={{
          width: '100%',
          overflow: 'auto',
          alignItems: 'center',
        }}
      >
        <Stack
          p={isPageView ? '40px 16px' : '56px 16px'} //to be aligned with the collapsed mode in the Canvas
          gap={3}
          sx={{
            maxWidth: AGENT_CARDS_CONTAINER_WIDTH,
            width: `min(${AGENT_CARDS_CONTAINER_WIDTH}, 100%)`,
          }}
        >
          <Stack gap={8}>
            <Stack gap={3}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <Typography variant="h2" color={colors.grey[900]}>
                  Your agents
                </Typography>
                {!showCreateNewAgentsSection && !renderLoading && canCreateNewAgents && (
                  <Stack minWidth="fit-content">
                    <NewAgentButton onNewAgentClick={onNewAgentClick} {...menuPopoverProps} />
                  </Stack>
                )}
              </Box>
              <AgentsGrid>
                {renderLoading && (
                  <>
                    <AgentCardSkeleton />
                    <AgentCardSkeleton />
                  </>
                )}
                {!renderLoading && (
                  <>
                    {existingAgents?.map((agent) => (
                      <AgentCard
                        key={agent.id}
                        agent={agent}
                        onClick={() =>
                          setAgentClickHandler({
                            agent,
                          })
                        }
                        opacity={menuPopoverProps.isPopoverOpen ? 0.6 : 1}
                      />
                    ))}
                    <UserSupportAgentCard section="chat" />
                  </>
                )}
              </AgentsGrid>
            </Stack>
            {showCreateNewAgentsSection && canCreateNewAgents && (
              <Stack gap={3}>
                <Typography variant="h2" color={colors.grey[900]}>
                  Create new agents
                </Typography>
                <AgentsGrid>
                  {renderLoading && (
                    <>
                      <AgentCardSkeleton />
                      <AgentCardSkeleton />
                    </>
                  )}
                  {!renderLoading && (
                    <>
                      {creatableAgentTypes?.map((type) => (
                        <CreateNewAgentCard
                          key={type}
                          agentType={type}
                          onClick={() => onNewAgentClick(type)}
                        />
                      ))}
                      <UserSupportAgentCard section="launch" />
                      {creatableAgentTypesNoSub?.map((type) => (
                        <CreateNewAgentCard key={type} agentType={type} isNotSubscribed={true} />
                      ))}
                      {singletonAgentsNoSub?.map((agent) => (
                        <CreateNewAgentCard
                          key={agent.id}
                          agentType={agent.type}
                          isNotSubscribed={true}
                        />
                      ))}
                    </>
                  )}
                </AgentsGrid>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AgentsHomeCardsExpanded;
